/* Cushion theme — global behaviour */
(() => {
  // Header shadow once the page scrolls
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  const closeDetails = (details) => {
    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) summary.focus();
  };

  // Close buttons inside <details>, and click-outside to dismiss
  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-close-details]');
    if (closer) {
      const details = closer.closest('details');
      if (details) closeDetails(details);
      return;
    }
    document.querySelectorAll('details[data-dismissable][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  // Escape closes any open drawer or panel
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-dismissable][open]').forEach(closeDetails);
  });

  // Mobile drawer: lock page scroll while open
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (mobileNav) {
    mobileNav.addEventListener('toggle', () => {
      document.documentElement.classList.toggle('scroll-lock', mobileNav.open);
      if (mobileNav.open) mobileNav.querySelector('.mobile-nav__panel a, .mobile-nav__panel summary')?.focus();
    });
  }

  // Search: focus the field when opened
  const search = document.querySelector('[data-header-search]');
  if (search) {
    search.addEventListener('toggle', () => {
      if (search.open) search.querySelector('input[type="search"]')?.focus();
    });
  }

  // Cart count — any script can dispatch `cart:updated` after changing the cart
  window.Cushion = window.Cushion || {};
  window.Cushion.refreshCartCount = async () => {
    try {
      const response = await fetch(`${window.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-cart-link]').forEach((el) => {
        el.setAttribute('aria-label', `Cart, ${cart.item_count} items`);
      });
    } catch (error) {
      console.error('Cart count refresh failed', error);
    }
  };
  document.addEventListener('cart:updated', window.Cushion.refreshCartCount);
})();

/* ---------- Toast ---------- */
(() => {
  let toast, timer;
  window.Cushion = window.Cushion || {};
  window.Cushion.toast = (message, { error = false } = {}) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.classList.toggle('toast--error', error);
    toast.innerHTML = '';
    const text = document.createElement('span');
    text.textContent = message;
    toast.appendChild(text);
    if (!error) {
      const link = document.createElement('a');
      link.href = window.routes.cart;
      link.className = 'btn btn--accent btn--small';
      link.textContent = 'View cart';
      toast.appendChild(link);
    }
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(timer);
    timer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
  };
})();

/* ---------- Add to cart without leaving the page ---------- */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-ajax-add]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  button?.classList.add('is-loading');
  try {
    const response = await fetch(`${window.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added. Try again.');
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    if (window.Cushion.cartDrawer) window.Cushion.openCart();
    else window.Cushion.toast(`${data.product_title} added to your cart`);
  } catch (error) {
    window.Cushion.toast(error.message, { error: true });
  } finally {
    button?.classList.remove('is-loading');
  }
});

/* ---------- Hero slider ---------- */
document.querySelectorAll('[data-slider]').forEach((slider) => {
  const track = slider.querySelector('[data-slider-track]');
  const slides = [...slider.querySelectorAll('[data-slide]')];
  const dots = [...slider.querySelectorAll('[data-slider-dot]')];
  const pauseBtn = slider.querySelector('[data-slider-pause]');
  if (!track || slides.length < 2) return;

  let index = 0;
  let timer = null;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const interval = parseInt(slider.dataset.interval, 10) || 6000;
  let paused = slider.dataset.autoplay !== 'true' || reduceMotion;

  const goTo = (i) => {
    index = (i + slides.length) % slides.length;
    track.scrollTo({ left: slides[index].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  const setActive = (i) => {
    index = i;
    dots.forEach((dot, d) => dot.setAttribute('aria-current', d === i ? 'true' : 'false'));
    slides.forEach((slide, s) => slide.toggleAttribute('inert', s !== i));
  };
  const start = () => { stop(); if (!paused) timer = setInterval(() => goTo(index + 1), interval); };
  const stop = () => { clearInterval(timer); timer = null; };

  let scrollTimer;
  track.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => setActive(Math.round(track.scrollLeft / track.clientWidth)), 60);
  }, { passive: true });

  slider.querySelector('[data-slider-prev]')?.addEventListener('click', () => { goTo(index - 1); start(); });
  slider.querySelector('[data-slider-next]')?.addEventListener('click', () => { goTo(index + 1); start(); });
  dots.forEach((dot, d) => dot.addEventListener('click', () => { goTo(d); start(); }));

  pauseBtn?.addEventListener('click', () => {
    paused = !paused;
    pauseBtn.classList.toggle('is-paused', paused);
    pauseBtn.setAttribute('aria-label', paused ? 'Play slides' : 'Pause slides');
    paused ? stop() : start();
  });
  if (reduceMotion && pauseBtn) { pauseBtn.classList.add('is-paused'); pauseBtn.setAttribute('aria-label', 'Play slides'); }

  slider.addEventListener('mouseenter', stop);
  slider.addEventListener('mouseleave', start);
  slider.addEventListener('focusin', stop);
  slider.addEventListener('focusout', start);

  // Theme editor: jump to a slide when it is selected
  document.addEventListener('shopify:block:select', (e) => {
    const i = slides.indexOf(e.target);
    if (i > -1) { paused = true; stop(); goTo(i); }
  });

  setActive(0);
  start();
});

/* ---------- Reel (customer stories) ---------- */
document.querySelectorAll('[data-reel]').forEach((reel) => {
  const track = reel.querySelector('[data-reel-track]');
  const prev = reel.querySelector('[data-reel-prev]');
  const next = reel.querySelector('[data-reel-next]');
  if (!track) return;
  const step = () => (track.querySelector('li')?.offsetWidth || 270) + 16;
  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 4;
    if (prev) prev.disabled = track.scrollLeft <= 4;
    if (next) next.disabled = track.scrollLeft >= max;
    reel.querySelector('.reel__nav')?.toggleAttribute('hidden', max <= 0);
  };
  prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
  next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();

  // Only one video plays at a time
  track.addEventListener('play', (e) => {
    track.querySelectorAll('video').forEach((v) => { if (v !== e.target) v.pause(); });
  }, true);
});

/* ---------- Money formatting (shared) ---------- */
window.Cushion = window.Cushion || {};
window.Cushion.formatMoney = (cents) => {
  const format = window.moneyFormat || 'Rs.{{amount}}';
  const value = cents / 100;
  const withCommas = (n, d) => n.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const strip = (s) => s.replace(/\.00$/, '').replace(/,00$/, '');
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withCommas(Math.round(value), 0);
      case 'amount_with_comma_separator': return strip(withCommas(value, 2).replace(/,/g, '#').replace('.', ',').replace(/#/g, '.'));
      case 'amount_no_decimals_with_comma_separator': return withCommas(Math.round(value), 0).replace(/,/g, '.');
      default: return strip(withCommas(value, 2));
    }
  });
};

/* ---------- Cart drawer & live cart updates ---------- */
(() => {
  window.Cushion = window.Cushion || {};
  const drawer = document.querySelector('[data-cart-drawer]');
  window.Cushion.cartDrawer = !!drawer;
  let lastFocus = null;

  const sectionIds = () => [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection);

  const applySections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const target = document.querySelector(`[data-cart-section="${id}"]`);
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`[data-cart-section="${id}"]`);
      if (target && fresh) target.innerHTML = fresh.innerHTML;
    });
  };

  window.Cushion.refreshCart = async () => {
    const ids = sectionIds();
    if (!ids.length) return;
    try {
      const res = await fetch(`${window.routes.root}?sections=${ids.join(',')}`);
      applySections(await res.json());
    } catch (e) { /* keep current markup */ }
  };

  window.Cushion.openCart = async () => {
    if (!drawer) { window.location.href = window.routes.cart; return; }
    await window.Cushion.refreshCart();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('scroll-lock');
    drawer.querySelector('.cart-drawer__panel')?.focus();
  };

  const closeCart = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('scroll-lock');
    lastFocus?.focus();
  };

  document.addEventListener('click', async (event) => {
    if (drawer && event.target.closest('[data-cart-link]')) {
      event.preventDefault();
      window.Cushion.openCart();
      return;
    }
    if (event.target.closest('[data-cart-close]')) { closeCart(); return; }

    const changeBtn = event.target.closest('[data-line-change]');
    if (!changeBtn) return;
    const line = changeBtn.closest('[data-line]');
    if (!line) return;
    line.classList.add('is-updating');
    try {
      const res = await fetch(`${window.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line.dataset.line), quantity: Number(changeBtn.dataset.lineChange), sections: sectionIds() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update the cart.');
      applySections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (error) {
      line.classList.remove('is-updating');
      window.Cushion.toast?.(error.message, { error: true });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer?.classList.contains('is-open')) closeCart();
  });
})();

/* ---------- Auto-submit (sort dropdown) ---------- */
document.addEventListener('change', (event) => {
  const el = event.target.closest('[data-auto-submit]');
  if (el && el.form) el.form.requestSubmit ? el.form.requestSubmit() : el.form.submit();
});

/* ---------- Predictive search ---------- */
(() => {
  const input = document.querySelector('[data-predictive-input]');
  const results = document.querySelector('[data-predictive]');
  if (!input || !results) return;
  let timer;
  let controller;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
    timer = setTimeout(async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${window.routes.root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const html = await res.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-results]');
        results.innerHTML = fresh ? fresh.outerHTML : '';
        input.setAttribute('aria-expanded', fresh ? 'true' : 'false');
      } catch (e) { /* aborted or offline */ }
    }, 220);
  });
})();

/* ---------- Kit finder quiz ---------- */
document.querySelectorAll('[data-finder]').forEach((root) => {
  const steps = [...root.querySelectorAll('[data-step]')];
  const progress = root.querySelector('[data-finder-progress]');
  const roomNames = { kitchen: 'Kitchen', living: 'Living room', bedroom: 'Bedroom', bathroom: 'Bathroom' };
  let current = 0;

  const show = (i) => {
    current = i;
    steps.forEach((s, n) => s.classList.toggle('is-active', n === i));
    if (progress) progress.style.width = `${Math.min(100, ((i + 1) / 3) * 100)}%`;
    const focusTarget = steps[i].matches('[data-step="result"]') ? steps[i] : steps[i].querySelector('input');
    focusTarget?.focus({ preventScroll: true });
  };

  const value = (name) => root.querySelector(`input[name="${name}"]:checked`)?.value;
  const rooms = () => [...root.querySelectorAll('input[name="rooms"]:checked')].map((i) => i.value);

  const showResult = () => {
    const age = value('age');
    const style = value('style');
    const picked = rooms();
    const msg = root.querySelector('[data-age-msg]');
    msg.textContent = msg.dataset[age] || '';
    msg.hidden = !msg.textContent;
    const wantsKit = style === 'all' || picked.length >= 3;
    root.querySelector('[data-result="kit"]').hidden = !wantsKit;
    root.querySelector('[data-result="build"]').hidden = wantsKit;
    const list = root.querySelector('[data-room-list]');
    list.innerHTML = '';
    picked.forEach((room) => {
      const li = document.createElement('li');
      const strong = document.createElement('strong');
      strong.textContent = roomNames[room];
      li.append(strong, document.createTextNode(list.dataset[room] || ''));
      list.appendChild(li);
    });
    show(steps.length - 1);
  };

  root.querySelectorAll('input[name="age"]').forEach((i) => i.addEventListener('change', () => setTimeout(() => show(1), 180)));
  root.querySelectorAll('input[name="rooms"]').forEach((i) => i.addEventListener('change', () => {
    root.querySelector('[data-finder-next]').disabled = rooms().length === 0;
  }));
  root.querySelector('[data-finder-next]')?.addEventListener('click', () => show(2));
  root.querySelectorAll('input[name="style"]').forEach((i) => i.addEventListener('change', () => setTimeout(showResult, 180)));
  root.querySelectorAll('[data-finder-back]').forEach((b) => b.addEventListener('click', () => show(Math.max(0, current - 1))));
  root.querySelector('[data-finder-restart]')?.addEventListener('click', () => {
    root.querySelectorAll('input').forEach((i) => { i.checked = false; });
    root.querySelector('[data-finder-next]').disabled = true;
    show(0);
  });
});
